// components/CreateSenderModal.jsx
import React, { useEffect, useState } from "react";
import {
  FaCheckCircle,
  FaEnvelope,
  FaSave,
  FaServer,
  FaTimes,
} from "react-icons/fa";

import {
  createEmailSender,
  updateEmailSender,
} from "../../../../../services/mailingServices";

const DEFAULT_FORM = {
  label: "",
  from_name: "Laurent VICTORIN",
  from_email: "",
  reply_to: "",
  smtp_host: "smtp.hostinger.com",
  smtp_port: 587,
  smtp_secure: false,
  smtp_user: "",
  daily_limit: 100,
  hourly_limit: 30,
  is_default: false,
  is_active: true,
};

export default function CreateSenderModal({ initial = null, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(DEFAULT_FORM);

  useEffect(() => {
    if (initial) {
      setForm({
        label: initial.label || "",
        from_name: initial.from_name || "Laurent VICTORIN",
        from_email: initial.from_email || "",
        reply_to: initial.reply_to || "",
        smtp_host: initial.smtp_host || "smtp.hostinger.com",
        smtp_port: initial.smtp_port || 587,
        smtp_secure: !!initial.smtp_secure,
        smtp_user: initial.smtp_user || initial.from_email || "",
        daily_limit: initial.daily_limit || 100,
        hourly_limit: initial.hourly_limit || 30,
        is_default: !!initial.is_default,
        is_active: initial.is_active !== false && initial.is_active !== 0,
      });
    } else {
      setForm(DEFAULT_FORM);
    }
  }, [initial]);

  const updateField = (key, value) => {
    setForm((prev) => {
      const next = {
        ...prev,
        [key]: value,
      };

      if (key === "from_email") {
        if (!prev.reply_to || prev.reply_to === prev.from_email) {
          next.reply_to = value;
        }

        if (!prev.smtp_user || prev.smtp_user === prev.from_email) {
          next.smtp_user = value;
        }

        if (!prev.label) {
          next.label = value ? `Expéditeur - ${value}` : "";
        }
      }

      return next;
    });
  };

  const handleSave = async () => {
    if (!form.label.trim()) {
      alert("Le libellé est obligatoire.");
      return;
    }

    if (!form.from_name.trim()) {
      alert("Le nom d’expéditeur est obligatoire.");
      return;
    }

    if (!form.from_email.trim()) {
      alert("L’adresse email d’expéditeur est obligatoire.");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        label: form.label.trim(),
        from_name: form.from_name.trim(),
        from_email: form.from_email.trim(),
        reply_to: form.reply_to.trim() || form.from_email.trim(),
        smtp_host: form.smtp_host.trim() || "smtp.hostinger.com",
        smtp_port: Number(form.smtp_port || 587),
        smtp_secure: !!form.smtp_secure,
        smtp_user: form.smtp_user.trim() || form.from_email.trim(),
        daily_limit: Number(form.daily_limit || 100),
        hourly_limit: Number(form.hourly_limit || 30),
        is_default: !!form.is_default,
        is_active: !!form.is_active,
      };

      const saved = isEdit
        ? await updateEmailSender(initial.id, payload)
        : await createEmailSender(payload);

      if (!saved) {
        throw new Error("Sauvegarde impossible.");
      }

      if (typeof onSaved === "function") {
        onSaved(saved);
      }

      onClose();
    } catch (error) {
      console.error(error);
      alert("Erreur lors de la sauvegarde du compte expéditeur.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/55" onClick={onClose} />

      <div className="relative w-[96vw] max-w-4xl max-h-[92vh] overflow-hidden rounded-3xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 dark:border-slate-800 p-5">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-200 px-3 py-1 text-xs font-medium">
              <FaEnvelope />
              Compte expéditeur
            </div>

            <h2 className="mt-3 text-xl font-semibold">
              {isEdit ? "Modifier un expéditeur" : "Créer un expéditeur"}
            </h2>

            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Configurez l’identité visible dans vos campagnes email.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <FaTimes />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] max-h-[calc(92vh-106px)] overflow-auto">
          <div className="p-5 space-y-5">
            <section className="rounded-3xl border border-slate-200 dark:border-slate-800 p-4">
              <div className="font-semibold">Identité d’envoi</div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Libellé interne">
                  <input
                    value={form.label}
                    onChange={(e) => updateField("label", e.target.value)}
                    placeholder="Ex : MonAppliClub - Principal"
                    className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm"
                  />
                </Field>

                <Field label="Nom affiché">
                  <input
                    value={form.from_name}
                    onChange={(e) => updateField("from_name", e.target.value)}
                    placeholder="Laurent VICTORIN"
                    className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm"
                  />
                </Field>

                <Field label="Email expéditeur">
                  <input
                    type="email"
                    value={form.from_email}
                    onChange={(e) => updateField("from_email", e.target.value)}
                    placeholder="contact@monappliclub.com"
                    className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm"
                  />
                </Field>

                <Field label="Répondre à">
                  <input
                    type="email"
                    value={form.reply_to}
                    onChange={(e) => updateField("reply_to", e.target.value)}
                    placeholder="contact@monappliclub.com"
                    className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm"
                  />
                </Field>
              </div>
            </section>

            <section className="rounded-3xl border border-slate-200 dark:border-slate-800 p-4">
              <div className="flex items-center gap-2 font-semibold">
                <FaServer className="text-slate-400" />
                Configuration SMTP
              </div>

              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Le mot de passe SMTP n’est pas saisi ici. Il reste dans le
                fichier <code>.env</code> du serveur.
              </p>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="SMTP host">
                  <input
                    value={form.smtp_host}
                    onChange={(e) => updateField("smtp_host", e.target.value)}
                    placeholder="smtp.hostinger.com"
                    className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm"
                  />
                </Field>

                <Field label="SMTP port">
                  <input
                    type="number"
                    value={form.smtp_port}
                    onChange={(e) => updateField("smtp_port", e.target.value)}
                    placeholder="587"
                    className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm"
                  />
                </Field>

                <Field label="SMTP user">
                  <input
                    value={form.smtp_user}
                    onChange={(e) => updateField("smtp_user", e.target.value)}
                    placeholder="contact@monappliclub.com"
                    className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm"
                  />
                </Field>

                <Field label="Sécurité">
                  <label className="flex items-center gap-2 rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.smtp_secure}
                      onChange={(e) =>
                        updateField("smtp_secure", e.target.checked)
                      }
                    />
                    SMTP sécurisé SSL/TLS
                  </label>
                </Field>
              </div>
            </section>

            <section className="rounded-3xl border border-slate-200 dark:border-slate-800 p-4">
              <div className="font-semibold">Limites d’envoi</div>

              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Ces limites serviront ensuite au worker d’envoi progressif.
              </p>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Limite par jour">
                  <input
                    type="number"
                    min="1"
                    value={form.daily_limit}
                    onChange={(e) => updateField("daily_limit", e.target.value)}
                    className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm"
                  />
                </Field>

                <Field label="Limite par heure">
                  <input
                    type="number"
                    min="1"
                    value={form.hourly_limit}
                    onChange={(e) =>
                      updateField("hourly_limit", e.target.value)
                    }
                    className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm"
                  />
                </Field>

                <label className="flex items-center gap-2 rounded-2xl border border-slate-200 dark:border-slate-800 p-4 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_default}
                    onChange={(e) =>
                      updateField("is_default", e.target.checked)
                    }
                  />
                  <span className="text-sm">Définir comme expéditeur par défaut</span>
                </label>

                <label className="flex items-center gap-2 rounded-2xl border border-slate-200 dark:border-slate-800 p-4 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) =>
                      updateField("is_active", e.target.checked)
                    }
                  />
                  <span className="text-sm">Compte actif</span>
                </label>
              </div>
            </section>
          </div>

          <aside className="border-t lg:border-t-0 lg:border-l border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 p-5">
            <div className="sticky top-0 space-y-4">
              <PreviewCard form={form} />

              <div className="rounded-3xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-4">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 text-white dark:bg-slate-100 dark:text-slate-950 px-4 py-3 text-sm font-medium disabled:opacity-50"
                >
                  <FaSave />
                  {saving ? "Sauvegarde…" : "Sauvegarder"}
                </button>

                <button
                  type="button"
                  onClick={onClose}
                  className="mt-2 w-full rounded-2xl bg-slate-100 dark:bg-slate-800 px-4 py-3 text-sm"
                >
                  Annuler
                </button>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="mb-1.5 text-xs font-medium text-slate-600 dark:text-slate-400">
        {label}
      </div>
      {children}
    </label>
  );
}

function PreviewCard({ form }) {
  return (
    <div className="rounded-3xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-4">
      <div className="font-semibold">Aperçu</div>

      <div className="mt-4 rounded-2xl border border-slate-200 dark:border-slate-800 p-4">
        <div className="flex items-start gap-3">
          <div className="rounded-2xl bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-200 p-3">
            <FaEnvelope />
          </div>

          <div className="min-w-0">
            <div className="font-semibold truncate">
              {form.from_name || "Nom expéditeur"}
            </div>

            <div className="text-sm text-slate-500 truncate">
              {form.from_email || "email@domaine.fr"}
            </div>

            {form.reply_to && (
              <div className="mt-1 text-xs text-slate-400 truncate">
                Réponse : {form.reply_to}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 space-y-2 text-sm">
        <Line label="SMTP" value={form.smtp_host || "—"} />
        <Line label="Port" value={form.smtp_port || "—"} />
        <Line label="User" value={form.smtp_user || "—"} />
        <Line
          label="Sécurisé"
          value={form.smtp_secure ? "Oui" : "Non"}
        />
        <Line label="Limite jour" value={form.daily_limit || "—"} />
        <Line label="Limite heure" value={form.hourly_limit || "—"} />
      </div>

      {form.is_default && (
        <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200 px-3 py-1 text-xs">
          <FaCheckCircle />
          Expéditeur par défaut
        </div>
      )}
    </div>
  );
}

function Line({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-800 pb-2">
      <span className="text-slate-500 dark:text-slate-400">{label}</span>
      <span className="font-medium text-right truncate">{value}</span>
    </div>
  );
}